import { useAuth as useOidcAuth } from 'react-oidc-context';

/**
 * 完全ログアウト処理
 * 1. ローカルセッションとストレージをクリア
 * 2. react-oidc-contextのユーザー情報を削除
 * 3. CognitoのホストUIログアウトを実行（オプション）
 */
export async function performCompleteLogout(): Promise<void> {
  try {
    // ローカルストレージのクリア
    try {
      if (typeof window !== 'undefined') {
        localStorage.clear();
      }
    } catch (error) {
      console.warn('LocalStorage クリアエラー:', error);
    }

    // セッションストレージのクリア
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.clear();
      }
    } catch (error) {
      console.warn('SessionStorage クリアエラー:', error);
    }

    // OIDCセッションキャッシュのクリア
    clearSessionCache();

    // CognitoログアウトURLを構築してリダイレクト
    const cognitoLogoutUrl = buildCognitoLogoutUrl();

    if (cognitoLogoutUrl && typeof window !== 'undefined') {
      window.location.href = cognitoLogoutUrl;
      return;
    }

    // フォールバック: サインインページへ直接リダイレクト
    if (typeof window !== 'undefined') {
      window.location.href = '/auth/signin';
    }
  } catch (error) {
    console.error('完全ログアウト処理エラー:', error);
    // 最後の手段として直接リダイレクト
    if (typeof window !== 'undefined') {
      window.location.href = '/auth/signin';
    }
  }
}

/**
 * Cognitoログアウト URL を構築
 * OIDC設定からauthorityを取得してCognitoドメインを抽出
 */
function buildCognitoLogoutUrl(): string | null {
  const authority = import.meta.env.VITE_OIDC_AUTHORITY;
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID;
  const redirectUri = window.location.origin;

  if (!authority || !clientId) {
    return null;
  }

  // Cognito authorityからドメインを抽出
  // 例: https://cognito-idp.us-east-1.amazonaws.com/us-east-1_xxx
  // → Cognito Hosted UIドメインは別途設定が必要な場合がある
  // ここではauthorityから推測できないため、nullを返す
  // 必要に応じて環境変数VITE_COGNITO_DOMAINを追加

  // 環境変数からCognitoドメインを取得（オプション）
  const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;

  if (cognitoDomain) {
    const logoutUrl =
      `https://${cognitoDomain}/logout?` +
      `client_id=${clientId}&` +
      `logout_uri=${encodeURIComponent(`${redirectUri}/auth/signin?logout=cognito`)}`;

    return logoutUrl;
  }

  return null;
}

/**
 * OIDCセッションキャッシュを完全にクリア
 * HTTPクッキー、LocalStorage、SessionStorageを全て削除
 */
export function clearSessionCache(): void {
  try {
    if (typeof window === 'undefined') return;

    // oidc-client-tsが使用するキーパターン
    const keysToRemove = ['oidc.user', 'oidc.access_token', 'oidc.id_token', 'oidc.refresh_token'];

    // 1. HTTPクッキーの削除
    keysToRemove.forEach((key) => {
      try {
        // 複数のパターンでクッキーを期限切れにして削除
        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure;`;
        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; samesite=lax;`;

        console.log(`🗑️ HTTPクッキー削除: ${key}`);
      } catch (error) {
        console.warn(`HTTPクッキー '${key}' の削除に失敗:`, error);
      }
    });

    // 2. 現在のドメインの全てのOIDC関連クッキーを検出して削除
    try {
      const allCookies = document.cookie.split(';');
      allCookies.forEach((cookie) => {
        const cookieName = cookie.split('=')[0].trim();
        if (cookieName.includes('oidc') || cookieName.includes('auth')) {
          // 複数のパターンで削除を試行
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; samesite=lax;`;
          console.log(`🗑️ 検出されたOIDCクッキー削除: ${cookieName}`);
        }
      });
    } catch (error) {
      console.warn('OIDCクッキー検索・削除エラー:', error);
    }

    // 3. LocalStorageとSessionStorageのOIDCキーをクリア
    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
        console.log(`🗑️ Storage削除: ${key}`);
      } catch (error) {
        console.warn(`キー '${key}' の削除に失敗:`, error);
      }
    });

    // oidc-client-tsは通常LocalStorageに`oidc.user:{authority}:{clientId}`形式で保存
    // 全てのoidc関連キーを削除
    try {
      const storageKeys = Object.keys(localStorage);
      storageKeys.forEach((key) => {
        if (key.startsWith('oidc.')) {
          localStorage.removeItem(key);
          console.log(`🗑️ LocalStorage削除: ${key}`);
        }
      });
    } catch (error) {
      console.warn('LocalStorage全削除エラー:', error);
    }

    console.log('✅ OIDCセッションキャッシュの完全クリアが完了しました');
  } catch (error) {
    console.error('セッションキャッシュクリアエラー:', error);
  }
}
